import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PaymentAPI, extractError } from "@/lib/api";

/**
 * GatewayCheckoutButton
 * - Stub mode: clicking calls /api/payments/gateway/init then immediately
 *   POSTs a fake "payment.captured" event to /api/payments/gateway/webhook/{provider}.
 *   The server applies the payment atomically via run_with_txn_retry.
 * - We surface the event_id on a hidden data-testid attribute so the E2E
 *   spec can replay it to prove idempotency.
 * - A "stub mode — no real charge" badge sits next to the button at all times.
 */
export function GatewayCheckoutButton({ invoice, provider = "razorpay_stub", onCaptured }) {
    const [busy, setBusy] = useState(false);
    const [lastEventId, setLastEventId] = useState(null);
    const [lastOrderId, setLastOrderId] = useState(null);

    const click = async () => {
        if (!invoice) return;
        if (!["issued", "partially_paid", "overdue"].includes(invoice.status)) {
            toast.error("Invoice is not payable.");
            return;
        }
        setBusy(true);
        try {
            const init = await PaymentAPI.gatewayInit({ invoice_id: invoice.id, method: `gateway_${provider.split("_")[0]}` });
            const orderId = init.provider_order?.id || init.provider_order?.order_id;
            const eventId = `evt_stub_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
            setLastOrderId(orderId);
            setLastEventId(eventId);
            const payload = {
                event_id: eventId,
                event_type: "payment.captured",
                payload: { order_id: orderId, payment_id: `pay_stub_${Date.now()}`, amount_minor: invoice.amount_due_minor },
            };
            const res = await PaymentAPI.gatewayWebhook(provider, payload);
            if (res.status >= 300 || res.body?.status === "failed") {
                throw new Error(res.body?.error?.message || "Gateway capture failed");
            }
            toast.success("Captured (stub mode)");
            onCaptured?.({ event_id: eventId, order_id: orderId, payload });
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="inline-flex items-center gap-2">
            <Button onClick={click} disabled={busy}
                    data-testid="gateway-checkout-btn"
                    data-last-event-id={lastEventId || ""}
                    data-last-order-id={lastOrderId || ""}
                    className="bg-brand-red text-white hover:bg-brand-redDeep">
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1 h-4 w-4" />}
                Pay via gateway
            </Button>
            <Badge variant="outline" className="border-amber-300 bg-amber-50 font-mono text-[10px] uppercase tracking-wider text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
                   data-testid="gateway-stub-badge">
                stub mode — no real charge
            </Badge>
            {lastEventId && (
                <span data-testid="gateway-last-event-id" className="hidden">{lastEventId}</span>
            )}
        </div>
    );
}
